// src/types.ts
import type { ConnectionState, IntegrationState } from '@apideck/unify/models/components/index.js';
import type { LoginMethod, OrgMemberInfo } from '@propelauth/node';
import type { Request } from 'express';
import type { OrganisationV2 } from '../organisation/organisation.entity.js';
import type { FactV2 } from '../matter/fact/fact.entity.js';
import type { FactSubsetRelevance } from '../matter/fact/fact-subset-relevance.entity.js';
import type { MetadataFieldFilter } from '../case/dto/metadata-filter.dto.js';

export interface User {
  userId: string;
  orgIdToOrgMemberInfo: {
    [key: string]: OrgMemberInfo;
  };
}

export interface CustomRequest extends Request {
  user?: User & { email: string; loginMethod: LoginMethod; orgId: string; userId: string };
}

export type PaginatedResponse<T> = {
  data: T[];
  pageCount: number;
  totalCount: number;
  totalUnfilteredCount?: number;
};

export type PlanStage = {
  name: string;
  description: string;
  parameters: PlanParameters[];
};

export type PlanParameters =
  | {
      name: string;
      type: 'str';
      value: string | null;
      available_values: string[];
      updated?: boolean;
    }
  | {
      name: string;
      type: 'boolean';
      value: boolean | null;
      available_values: string[];
      updated?: boolean;
    }
  | {
      name: string;
      type: 'List[single-select,fixed-list]';
      value: Option[] | null;
      available_values: Option[];
      updated?: boolean;
    }
  | {
      name: string;
      type: 'List[multi-select,fixed-list]';
      value: Option[] | null;
      available_values: Option[];
      all_selection?: boolean;
      updated?: boolean;
    }
  | {
      name: string;
      type: 'List[multi-select,any-value]';
      value: Option[] | null;
      available_values: Option[];
      updated?: boolean;
    };

export interface Option {
  label: string;
  value: string;
}

export enum DraftType {
  Claim = 'claim',
  Rebuttal = 'rebuttal',
  Deposition = 'deposition',
}

export type Environment = 'production' | 'development' | 'testing' | 'local';

export enum MessageType {
  User = 'user',
  Internal = 'internal',
  Assistant = 'assistant',
}

export enum FlowType {
  DraftFlow = 'draft_flow',
  ExtractionFlow = 'extraction_flow',
  QuestionFlow = 'question_flow',
  ManualFlow = 'manual_flow',
}

export interface Artifact {
  file_name: string;
  extension: string;
  s3_key: string;
  s3_bucket: string;
}

export interface MessageConfig {
  trafficLight?: TrafficLight;
  flowType?: FlowType;
  flowName?: string;
  artifacts?: Artifact[];
  followUpQuestions?: string[];
  plan?: Record<string, unknown>[];
  docIds?: string[];
  draftType?: DraftType | null;
}

export enum TrafficLight {
  Green = 'green',
  Amber = 'amber',
  Red = 'red',
}

export enum ThreadStatus {
  NotStarted = 'notStarted',
  PlanLoading = 'planLoading',
  PlanLoaded = 'planLoaded',
  PlanExecuting = 'planExecuting',
  PlanComplete = 'planComplete',
  Failed = 'failed',
}

export enum Relevance {
  Relevant = 'relevant',
  NotRelevant = 'notRelevant',
  MaybeRelevant = 'maybeRelevant',
}

export enum Classification {
  Primary = 'primary',
  Secondary = 'secondary',
  Future = 'future',
  Litigation = 'litigation',
}

export interface Comment {
  id: string;
  caseId: string;
  comment: string;
  commentBy: string;
  commentDate: Date;
}

export interface DateInfo {
  dateIsAmbiguous: boolean;
  dateIsAmbiguousText: string | null;
  isFutureDate: string | null;
}

export enum StyleFormat {
  Table = 'table',
  Written = 'written',
}

export enum DownloadFormat {
  DOCX = 'docx',
  EXCEL = 'xlsx',
  CSV = 'csv',
}

export enum ChronologyType {
  KeyFacts = 'keyFacts',
  RelevantFacts = 'relevantFacts',
}

export interface KimPollingMessage {
  message: string;
  type: string;
  stage?: string;
}

export enum MatterType {
  Personal = 'personal',
  General = 'general',
}

export interface ApideckConnection {
  id: string;
  service_id: string;
  name: string;
  unified_api: string;
  state: ConnectionState;
  integration_state: IntegrationState;
  enabled: boolean;
  icon: string;
  logo: string;
  authorize_url: string;
}

export enum EmailTemplateOptions {
  UserAddedToMatter = 'user-added-to-matter',
  Invite = 'invite',
  TaggedInComment = 'tagged-in-comment',
  MatterApprovalRequired = 'matter-approval-required',
  MatterAdminApprovalRequired = 'matter-approval-required-admin',
  MatterProcessingComplete = 'matter-processing-complete',
  MatterProcessingCompleteUpdate = 'matter-processing-complete-update',
  SubsetProcessingComplete = 'subset-processing-complete',
  SubsetProcessingCompleteUpdate = 'subset-processing-complete-update',
  MatterAccessRequested = 'matter-access-requested',
}

export interface UsageMetrics {
  chronologies: number;
  runs: number;
  pages: number;
  facts: number;
  threads: number;
}

export interface UsageDataObject {
  billable: UsageMetrics;
  nonBillable: UsageMetrics;
}

export interface UsageMetricsWithTokens extends UsageMetrics {
  tokens: number;
}

export interface UsageDataObjectWithTokens {
  billable: UsageMetricsWithTokens;
  nonBillable: UsageMetricsWithTokens;
}

export interface BillingPeriodCreditUsage {
  billable: number;
  nonBillable: number;
  maxCredits?: number;
  startDate: Date;
  endDate: Date;
  isUsingTrialPackage: boolean;
}

export interface TableUsageData {
  [id: string]: UsageMetricsWithTokens & {
    matterName?: string;
    matterCode?: string;
    userEmail?: string;
    userDisabled?: boolean;
    matterType?: MatterType;
    matterCreatedBy?: string;
  };
}

export interface UsageData {
  id: string;
  organisationId: string;
  userId: string;
  matterId: string;
  date: Date;
  data: UsageDataObject;
}

export enum ChecklistStep {
  CreateMatter = 'create-matter',
  UploadDocuments = 'upload-documents',
  CreateCase = 'create-case',
  UseKim = 'use-kim',
}

export enum RunStatus {
  InQueue = 'inQueue',
  Parsing = 'parsing',
  AwaitingApproval = 'awaitingApproval',
  Extracting = 'extracting',
  Clustering = 'clustering',
  IdentifyingRelevance = 'identifyingRelevance',
  IdentifyingInconsistencies = 'identifyingInconsistencies',
  Summarising = 'summarising',
  Complete = 'complete',
  Failed = 'failed',
  Cancelled = 'cancelled',
  ApprovalTimeout = 'approvalTimeout',
}

export interface DurationEstimate {
  minutes?: number;
  hours?: number;
  days?: number;
}

export enum KimRequestType {
  GeneratePlan = 'generate_plan',
  ExecutePlan = 'execute_plan',
}

export enum ForbiddenStatuses {
  ForbiddenPersonalMatter = 'ForbiddenPersonalMatter',
  ForbiddenNotInOrg = 'ForbiddenNotInOrg',
  ForbiddenNoAccess = 'ForbiddenNoAccess',
}

export enum Fonts {
  TimesNewRoman = 'Times New Roman',
  Arial = 'Arial',
}

export enum TextAlignment {
  Left = 'left',
  Both = 'both',
}

export interface DocumentDownloadOptions {
  fontFamily: Fonts;
  textAlignment: TextAlignment;
  includeReferences: boolean;
}

export enum PersonType {
  Person = 'person',
  Organisation = 'organisation',
  Place = 'place',
}

export enum Jurisdiction {
  EnglandAndWales = 'England & Wales',
  USFederal = 'US Federal',
}

export type ReferencesDict = Record<string, { id: string; type: 'fact' | 'chunk' }>;

export enum DocProcessingErrorType {
  FactExtractError = 'factExtractError',
  FileError = 'fileError',
  TextractError = 'textractError',
}

export type DocumentDownloadType = 'original' | 'highlighted' | 'asPDF';

export enum KimProgressMessageType {
  TaskStart = 'task_start',
  TaskMessage = 'task_message',
  TaskComplete = 'task_complete',
  AllComplete = 'all_complete',
}
export interface KimProgressMessage {
  message: string;
  type: KimProgressMessageType;
  stage?: string;
}

export enum UploadFailureReason {
  Duplicate = 'duplicate',
  UnsupportedFileFormat = 'unsupportedFileFormat',
  NoFileAvailable = 'noFileAvailable',
  DownloadFailed = 'downloadFailed',
  StorageError = 'storageError',
}

export type OrganisationWithCounts = OrganisationV2 & { userCount: number; matterCount: number };

export interface Interval {
  days?: number | undefined;
  hours?: number | undefined;
  minutes?: number | undefined;
}

export enum DisputeStatus {
  PreAction = 'Pre-action',
  Claim = 'Claim',
  Trial = 'Trial',
  PostTrial = 'Post-trial',
}

// Once 2.0 migration complete, the deprecated fields from FactV2 can be dropped, and we can just merge the two entities
export type SubsetFact = Omit<FactV2, 'isKey' | 'relevance' | 'significance'> &
  Pick<FactSubsetRelevance, 'isKey' | 'relevance' | 'significance'>;

export interface MatterDocsFilters {
  processed?: boolean;
  extensions?: string[];
  startDate?: string;
  endDate?: string;
  documentTypes?: string[];
  documentThemes?: string[];
  hasKeyFacts?: boolean;
  hasRelevantFacts?: boolean;
  searchQuery?: string;
  personNames?: string[];
  runIds?: string[];
  personIds?: string[];
  author?: string;
  metadataFilters?: MetadataFieldFilter[];
  docIds?: string[];
}

export type RunsFilter = Array<{
  id: string;
  createdDate: Date;
}>;
export type PersonsFilter = Array<{
  id: string;
  name: string;
}>;

export interface MatterDocFilterOptions {
  types: string[];
  extensions: string[];
  issues: string[];
  persons: PersonsFilter;
  runs: RunsFilter;
}
